/* <rr-cart-drawer> — carrito en panel lateral.
   Se abre desde el enlace de carrito del header y al añadir una pieza.
   Cantidades y borrado van por /cart/change.js y el panel se repinta con la
   Section Rendering API, sin recargar la página. Sin dependencias. */
(() => {
  if (customElements.get('rr-cart-drawer')) return;

  // con el prefijo del idioma (/es/, /fr/…), o el panel repinta en inglés
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // medición (rr_cart, rr_qty_change, docs/plan-de-medicion.md §4.8)
  const rr = (n, p) => window.rrTrack && window.rrTrack(n, p);

  class RRCartDrawer extends HTMLElement {
    connectedCallback() {
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-rr-cart-open]');
        if (opener) { e.preventDefault(); this.open('header'); }
      });

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-rr-drawer-close]')) { this.close(); return; }
        const step = e.target.closest('[data-rr-qty]');
        if (step) {
          const input = this.querySelector(`[data-rr-count][data-line="${step.dataset.line}"]`);
          this.change(step.dataset.line, Math.max(0, parseInt(input.value, 10) + Number(step.dataset.rrQty)));
          return;
        }
        const remove = e.target.closest('[data-rr-remove]');
        if (remove) this.change(remove.dataset.line, 0);
      });

      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-rr-count]');
        if (input) this.change(input.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      // tras añadir una pieza, el panel se abre ya actualizado
      document.addEventListener('rr:cart-updated', () => this.refresh(true));
    }

    // trigger: header (el enlace del carrito) o add (al añadir una pieza)
    open(trigger) {
      if (!this.openedAt) {
        this.openedAt = Date.now();
        const count = document.querySelector('[data-rr-cart-count]');
        rr('rr_cart', { action: 'open', trigger: trigger || 'add', cart_items: count ? parseInt(count.textContent, 10) || 0 : undefined }); // no «items»: es la lista de piezas de GA4
      }
      this.hidden = false;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.querySelector('[data-rr-drawer-close]')?.focus();
      });
      document.documentElement.style.overflow = 'hidden';
    }

    close() {
      if (this.openedAt) {
        rr('rr_cart', { action: 'close', open_ms: Date.now() - this.openedAt });
        this.openedAt = 0;
      }
      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => { this.hidden = true; }, 300);
    }

    async change(line, quantity) {
      const input = this.querySelector(`[data-rr-count][data-line="${line}"]`);
      const from = input ? parseInt(input.defaultValue, 10) : undefined; // la cantidad pintada
      this.style.opacity = '0.6';
      try {
        const res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line, 10), quantity, sections: this.dataset.sectionId }),
        });
        // 422: no quedan tantas (pieza NUMBERED al final de su tirada). El
        // panel se repinta con lo que sí hay, sin mandar al carrito.
        if (res.status === 422) {
          rr('rr_cart_error', { reason: 'limit', ui_area: 'drawer' });
          await this.refresh(false);
          this.style.opacity = '';
          return;
        }
        if (!res.ok) throw new Error('change failed');
        const cart = await res.json();
        this.swap(cart.sections?.[this.dataset.sectionId]);
        document.querySelectorAll('[data-rr-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
        });
        // aviso para el resto de la página (escalera de stack); no reabre el panel
        document.dispatchEvent(new CustomEvent('rr:cart-changed', { detail: { cart } }));
        rr('rr_qty_change', { from, to: quantity, ui_area: 'drawer' });
      } catch {
        location.href = `${root}cart`;
      }
      this.style.opacity = '';
    }

    async refresh(thenOpen) {
      try {
        const res = await fetch(`${root}?sections=${this.dataset.sectionId}`);
        const data = await res.json();
        this.swap(data[this.dataset.sectionId]);
      } catch {
        /* se queda el contenido anterior */
      }
      if (thenOpen) document.querySelector('rr-cart-drawer')?.open();
    }

    swap(html) {
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('rr-cart-drawer');
      if (!fresh) return;
      const wasOpen = this.classList.contains('is-open');
      this.innerHTML = fresh.innerHTML;
      if (wasOpen) this.classList.add('is-open');
      else this.hidden = true;
    }
  }

  customElements.define('rr-cart-drawer', RRCartDrawer);
})();
